import { type FormEvent, type KeyboardEvent, useEffect, useId, useState } from 'react';
import { useSearchUsers } from '@/hooks/useUsers';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { Alert } from '@/components/ui/Alert';
import { getApiErrorMessage } from '@/api/client';
import type { User } from '@/api/types';

export interface AssignUserPayload {
  userId?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
}

interface Props {
  actionLabel: string;
  isPending: boolean;
  error: unknown;
  onAssign: (payload: AssignUserPayload) => Promise<unknown>;
}

type Field = 'firstName' | 'lastName' | 'email';

const emptyValues = { firstName: '', lastName: '', email: '' };

/**
 * Assigns a judge/competitor to a competition. Typing in any of the three
 * fields searches existing accounts (GET /users/search) and suggests them as
 * "ime priimek, e-pošta"; picking one fills all fields and assigns that
 * account. Anything typed without picking a suggestion creates the account on
 * the fly and assigns it; the person gets an email to choose their own password.
 */
export function AssignUserForm({ actionLabel, isPending, error, onAssign }: Props) {
  const listId = useId();
  const [values, setValues] = useState<Record<Field, string>>({ firstName: '', lastName: '', email: '' });
  const [focused, setFocused] = useState<Field | null>(null);
  const [selected, setSelected] = useState<User | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [active, setActive] = useState(0);

  const search = useSearchUsers(searchTerm);
  const results = search.data ?? [];
  const showList = focused !== null && selected === null && searchTerm.length > 0 && results.length > 0;

  const typed = focused ? values[focused].trim() : '';
  useEffect(() => {
    const timeout = setTimeout(() => setSearchTerm(typed), 250);
    return () => clearTimeout(timeout);
  }, [typed]);

  useEffect(() => setActive(0), [searchTerm]);

  function change(field: Field, value: string) {
    // Editing after picking a suggestion turns it back into a new-account entry.
    setSelected(null);
    setValues((v) => ({ ...v, [field]: value }));
  }

  function pick(u: User) {
    setSelected(u);
    setValues({ firstName: u.firstName ?? '', lastName: u.lastName ?? '', email: u.email });
    setSearchTerm('');
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (!showList) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      pick(results[active]);
    } else if (e.key === 'Escape') {
      setSearchTerm('');
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    try {
      if (selected) {
        await onAssign({ userId: selected.id });
      } else {
        await onAssign({
          email: values.email.trim(),
          firstName: values.firstName.trim(),
          lastName: values.lastName.trim(),
        });
      }
    } catch {
      // The error is shown above the form (via the `error` prop). The typed values stay so they can be corrected.
      return;
    }
    setSelected(null);
    setValues(emptyValues);
  }

  const canSubmit =
    selected !== null ||
    Boolean(values.email.trim() && values.firstName.trim() && values.lastName.trim());

  function field(name: Field, label: string, type?: string) {
    return (
      <div className="relative min-w-48 flex-1">
        <Input
          label={label}
          type={type}
          value={values[name]}
          autoComplete="off"
          role="combobox"
          aria-expanded={showList && focused === name}
          aria-controls={listId}
          aria-autocomplete="list"
          required
          onChange={(e) => change(name, e.target.value)}
          onFocus={() => setFocused(name)}
          onBlur={() => setFocused((f) => (f === name ? null : f))}
          onKeyDown={handleKeyDown}
        />
        {showList && focused === name && (
          <ul
            id={listId}
            role="listbox"
            className="absolute z-10 mt-1 max-h-72 w-full min-w-72 overflow-auto rounded-control border border-line bg-surface-3 py-1 shadow-card"
          >
            {results.map((u, i) => (
              <li
                key={u.id}
                role="option"
                aria-selected={i === active}
                className={`cursor-pointer px-3 py-2 text-sm text-fg ${i === active ? 'bg-surface-2' : ''}`}
                onMouseEnter={() => setActive(i)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(u);
                }}
              >
                {`${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || u.displayName}
                <span className="text-fg-muted">, {u.email}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-3">
      {error !== undefined && error !== null && <Alert variant="error">{getApiErrorMessage(error)}</Alert>}
      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
        {field('firstName', 'Ime')}
        {field('lastName', 'Priimek')}
        {field('email', 'E-pošta', 'email')}
        <Button type="submit" isLoading={isPending} disabled={!canSubmit}>
          {selected ? actionLabel : `Ustvari račun in ${actionLabel.toLowerCase()}`}
        </Button>
      </div>
      {selected ? (
        <p className="text-xs text-success">Izbran obstoječ račun: {selected.email}.</p>
      ) : (
        <p className="text-xs text-fg-muted">
          Začnite tipkati za iskanje obstoječih računov. Če ne izberete predloga, bo ustvarjen nov račun, uporabnik pa
          prejme e-pošto s povezavo, kjer si sam izbere geslo.
        </p>
      )}
    </form>
  );
}
